import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { baseUrl, getConfig } from "../../../../serverUrl";
import TopPrpertiesDisplay from "./_topDisplay";
import PropertiesCardList from "./_propertycardlist";
import UnitTableView from "./_unittableview.jsx";
import {
    useGetLandlordPropertyQuery,
    useGetAllUnitsQuery, 
    useChangeUnitDetailsMutation
} from "../../lib/apiSlice/property/propertySlice.tsx";

export default function PropertiesPage() {
  
    const { data: landlordProperties, isLoading: propertiesLoading } =
        useGetLandlordPropertyQuery();
    
    const { data: allUnitsData, isLoading: unitsLoading } = 
        useGetAllUnitsQuery(); 

    const [changeUnitDetails] = useChangeUnitDetailsMutation();

    const [filters, setFilters] = useState({
        search: "",
        location: "",
        type: "",
    });

    const [viewMode, setViewMode] = useState("card");

    // get property data
    const propertyResults = landlordProperties?.results || [];

    // Create a property lookup map with multiple possible ID fields
    const propertyMap = useMemo(() => {
        const map = new Map();
        propertyResults.forEach(property => {
            // Try multiple possible ID fields
            const possibleIds = [
                property.id,
                property.apartment_id,
                property.property_id,
                String(property.id),
                String(property.apartment_id),
                String(property.property_id)
            ].filter(Boolean);
            
            // Map all possible ID variations to this property
            possibleIds.forEach(id => {
                if (id) map.set(id, property);
            });
        });
        return map;
    }, [propertyResults]);

    // Normalize and enrich units with property names
    const allUnits = useMemo(() => {
        const rawUnits = Array.isArray(allUnitsData) ? allUnitsData : (allUnitsData?.results || []);
        
        // Debug: log a sample unit to see its structure
        if (rawUnits.length > 0) {
            console.log('Sample unit structure:', rawUnits[0]);
        }
        
        // Enrich each unit with the property name
        return rawUnits.map(unit => {
            // Try to find property using multiple possible ID fields
            const possiblePropertyIds = [
                unit.apartment_id,
                unit.property_id,
                unit.apartment,
                String(unit.apartment_id),
                String(unit.property_id),
                String(unit.apartment)
            ].filter(Boolean);
            
            let property = null;
            for (const id of possiblePropertyIds) {
                property = propertyMap.get(id);
                if (property) break;
            }
            
            // Get property name with fallbacks
            const propertyName = property?.name || 
                                property?.property_name || 
                                unit.property_name || 
                                'Unknown Property';
            
            return {
                ...unit,
                property_name: propertyName,
                // Keep original ID for debugging
                _debug_apartment_id: unit.apartment_id,
                _debug_property_id: unit.property_id
            };
        });
    }, [allUnitsData, propertyMap]);

    // combine loading states
    const isLoading = viewMode === "card" ? propertiesLoading : unitsLoading;

    // callback preventing unnecessary re-renders
    const handleFilterChange = useCallback((newFilters) => {
        setFilters((prev) => ({ ...prev, ...newFilters })); 
    }, []);

    // Filter properties for card view
    const filteredProperties = propertyResults.filter((property) => {
        if (filters.location && property.location !== filters.location) return false;
        if (filters.type && property.payment_status !== filters.type) return false;
        const searchLower = filters.search?.toLowerCase();
        if (filters.search && !property.name?.toLowerCase().includes(searchLower)) return false;
        return true;
    });

  
    const filteredUnits = useMemo(() => {
        return allUnits.filter((unit) => { 
            // Location filter 
            if (filters.location && unit.location !== filters.location) return false;
                
            // Type filter
            if (filters.type) {
                if (filters.type === "paid" && unit.rent_payment_status !== "paid") return false;
                if (filters.type === "partial" && unit.rent_payment_status !== "partial") return false;
                if (filters.type === "unpaid" && unit.rent_payment_status !== "unpaid") return false;
                if (filters.type === "vacant" && unit.status !== "vacant") return false;
                if (filters.type === "occupied" && unit.status === "vacant") return false;
            }
            
            // Search filter
            const searchLower = filters.search?.toLowerCase();
            if (filters.search && searchLower) {
                const matchesSearch =
                    unit.house?.toLowerCase().includes(searchLower) ||
                    unit.tenant?.toLowerCase().includes(searchLower) ||
                    unit.property_name?.toLowerCase().includes(searchLower);
                if (!matchesSearch) return false;
            }
            return true;
        });
    }, [allUnits, filters]);

    // Handle delete action
    const handleDeleteUnit = async (unit) => {
        try {
            await changeUnitDetails({
                houseId: unit.house_id, 
                bodyCredentials: { is_visible: false }
            }).unwrap();
        } catch (error) {
            console.error("Failed to delete unit:", error);
        }
    };

    const exportOpeningBalances = async () => {
        try {
            const config = getConfig();
            const response = await fetch(`${baseUrl}/landlord/export-house-opening-balances/`, {
                headers: config.headers || {},
            });

            if (!response.ok) {
                throw new Error("Failed to export opening balances");
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `house_opening_balances_${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
        }
    };
    
    const roundUp = (value) => Math.ceil(value);

    return (
        <div>
            <Helmet>
                <title>Properties</title>
                <meta name="description" content="Manage your properties here." />
            </Helmet>

            <div>
                <TopPrpertiesDisplay
                    properties={propertyResults}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    viewMode={viewMode}
                />

                <div className="flex flex-wrap justify-between items-center gap-3 mt-2 mb-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <Link to="/add-apartment" className="flex justify-between items-center">
                            <button className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded-md text-white transition-colors">
                                Add Property
                            </button>
                        </Link>

                        <button
                            onClick={exportOpeningBalances}
                            className="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-md text-white transition-colors"
                        >
                            Export Opening Balances
                        </button>
                    </div>

                    <button
                        onClick={() => {
                            const newViewMode = viewMode === "card" ? "table" : "card";
                            setViewMode(newViewMode);
                            if (newViewMode === "card") {
                                setFilters((prev) => ({ ...prev, type: "" }));
                            }
                        }}
                        className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-md text-gray-700 transition-colors"
                    >
                        {viewMode === "card" ? "Switch to Units View" : "Switch to Properties View"}
                    </button>
                </div>

                {isLoading ? (
                    <div className="text-center py-8 text-gray-500">
                        {viewMode === "card" ? "Loading properties..." : "Loading units..."}
                    </div>
                ) : viewMode === "card" ? (
                    <PropertiesCardList properties={filteredProperties} />
                ) : (
                    <UnitTableView
                        units={filteredUnits}
                        houseFilters={filters}
                        onDelete={handleDeleteUnit}
                        roundUp={roundUp}
                    />
                )}
            </div>
        </div>
    );
}
